import {css} from '@emotion/react';
import {MenuItem, SelectChangeEvent, Typography, useTheme} from '@mui/material';
import {GetStylesFunction, DlMuiSelect} from '@wallaby-ui/core';
import {IHeaderParams} from 'ag-grid-community';
import React, {useState} from 'react';
import {FormattedMessage} from 'react-intl';

import {FILTER_AND_SEARCH_REDUCER_STATE_ID} from 'src/js/containers/filter_and_search/filter_and_search_constants';
import {getGridStateId} from 'src/js/containers/grid/grid_helpers';
import {changeMemorizeColumnStates} from 'src/js/containers/grid/grid_thunks';
import {HOSPITALIZATIONS_FEATURE_NAME} from 'src/js/containers/hospitalizations/hospitalizations_constants';
import {useAppDispatch, useAppSelector} from 'src/js/store_hooks';

const COLUMN_PADDINGS = 24;

enum HospitalizationPatientsDepartmentOptionEnum {
  Current = 'Current',
  Admission = 'Admission',
}

interface HospitalizationPatientsDepartmentOption {
  id: HospitalizationPatientsDepartmentOptionEnum;
  label: string;
}

const hospitalizationPatientsDepartmentOptions: HospitalizationPatientsDepartmentOption[] =
  [
    {
      id: HospitalizationPatientsDepartmentOptionEnum.Current,
      label: 'patient.list.grid.currentDepartment',
    },
    {
      id: HospitalizationPatientsDepartmentOptionEnum.Admission,
      label: 'patient.list.grid.admissionDepartment',
    },
  ];

const ADMISSION_DEPARTMENT = 'lastEncounterSub.organization';
const ADMISSION_DATE = 'lastEncounterSub.startedAt';
const CURRENT_DEPARTMENT = 'encounter.organization';
const CURRENT_DATE = 'encounter.startedAt';

export interface ICustomHeaderParams extends IHeaderParams {
  menuIcon: string;
}

export const HospitalizationPatientsHeaderFormatter: React.FC<
  ICustomHeaderParams
> = (props) => {
  const {column} = props;
  const getInitialSelectValue = (columnStates) => {
    return columnStates?.[ADMISSION_DEPARTMENT]?.hide
      ? HospitalizationPatientsDepartmentOptionEnum.Admission
      : HospitalizationPatientsDepartmentOptionEnum.Current;
  };
  const theme = useTheme();

  const styles = getStyles({theme, params: {actualWidth: column.actualWidth}});
  const hospitalizationGridConfig = 'feature.hospitalizations.list.grid';
  const gridStateId = getGridStateId(hospitalizationGridConfig);

  const dispatch = useAppDispatch();
  const gridState = useAppSelector((state) => state[gridStateId]);
  const memorizedColumnStates = gridState?.memorizedColumnStates;
  const [selectValue, setSelectValue] = useState<string>(
    getInitialSelectValue(memorizedColumnStates)
  );

  const handleChange = (event: SelectChangeEvent<string>) => {
    setSelectValue(event.target.value);

    const isCurrent =
      event.target.value ===
      HospitalizationPatientsDepartmentOptionEnum.Current;

    if (memorizedColumnStates) {
      const columnStateChange = {
        ...memorizedColumnStates,
        [ADMISSION_DEPARTMENT]: {
          ...memorizedColumnStates[ADMISSION_DEPARTMENT],
          hide: !isCurrent,
        },
        [ADMISSION_DATE]: {
          ...memorizedColumnStates[ADMISSION_DATE],
          hide: !isCurrent,
        },
        [CURRENT_DEPARTMENT]: {
          ...memorizedColumnStates[CURRENT_DEPARTMENT],
          hide: isCurrent,
        },
        [CURRENT_DATE]: {
          ...memorizedColumnStates[CURRENT_DATE],
          hide: isCurrent,
        },
      };

      dispatch(
        changeMemorizeColumnStates(
          gridStateId,
          FILTER_AND_SEARCH_REDUCER_STATE_ID,
          HOSPITALIZATIONS_FEATURE_NAME,
          columnStateChange
        )
      );
    }
  };

  return (
    <div>
      <DlMuiSelect
        inputCss={styles.select}
        variant='standard'
        value={selectValue}
        onChange={handleChange}
        data-name={'hospitalization-patients-header-formatter--select'}
      >
        {hospitalizationPatientsDepartmentOptions.map((option) => (
          <MenuItem value={option.id} key={option.id}>
            <Typography css={styles.menuLabel} variant={'label'}>
              <FormattedMessage id={option.label} />
            </Typography>
          </MenuItem>
        ))}
      </DlMuiSelect>
    </div>
  );
};

HospitalizationPatientsHeaderFormatter.displayName =
  'HospitalizationPatientsHeaderFormatter';

const getStyles: GetStylesFunction = (options) => ({
  menuLabel: css`
    padding-left: 8px;
  `,
  select: css`
    width: ${(options?.params?.actualWidth ?? 0) - COLUMN_PADDINGS}px;
    color: ${options?.theme?.palette.custom.coolGray['900']};
    border-color: ${options?.theme?.palette.custom.coolGray['200']};
    background: white;
    text-align: left;
  `,
});
